import { useEffect, useState, useCallback } from 'react'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { UpgradeBanner } from '../components/ui/UpgradeBanner'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface AssetTag {
  id: string; short_code: string; tag_number: string | null; device_id: string | null
  device_name: string | null; site_name: string | null; status: string; batch_id: string | null
  assigned_at: string | null; created_at: string
}

interface TagBatch {
  id: string; name: string | null; quantity: number; prefix: string
  tags_assigned: number; tags_in_stock: number; created_at: string
}

interface DeviceWarranty {
  id: string; device_id: string; device_name: string | null; site_name: string | null
  manufacturer: string | null; warranty_type: string; start_date: string; end_date: string | null
  status: string; covers_parts: number; covers_labor: number
}

interface Stats {
  total_tags: number; assigned_tags: number; unassigned_tags: number
  expiring_warranties: number; recent_scans: number
}

type Tab = 'tags' | 'warranties' | 'batches'

export default function Assets() {
  const { enabled, loading: flagLoading, requiredPlan } = useFeatureFlag('asset_tracking')
  const { toast, toastError } = useToast()

  const [tab, setTab] = useState<Tab>('tags')
  const [stats, setStats] = useState<Stats | null>(null)
  const [tags, setTags] = useState<AssetTag[]>([])
  const [batches, setBatches] = useState<TagBatch[]>([])
  const [warranties, setWarranties] = useState<DeviceWarranty[]>([])
  const [loading, setLoading] = useState(true)
  const [showGenModal, setShowGenModal] = useState(false)
  const [selectedTag, setSelectedTag] = useState<AssetTag | null>(null)
  const [editingTag, setEditingTag] = useState(false)
  const [editForm, setEditForm] = useState({ status: '', device_id: '' })

  // Generate batch form
  const [genForm, setGenForm] = useState({ quantity: 25, prefix: 'TAG', starting_number: 1, name: '' })
  const [generating, setGenerating] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const [statsData, tagsData, batchesData, warrantiesData] = await Promise.all([
        api.get<Stats>('/asset-tracking/stats').catch(() => null),
        api.get<{ data: AssetTag[] }>('/asset-tracking/tags?limit=100').catch(() => ({ data: [] })),
        api.get<TagBatch[]>('/asset-tracking/batches').catch(() => []),
        api.get<DeviceWarranty[]>('/asset-tracking/warranties').catch(() => []),
      ])
      if (statsData) setStats(statsData)
      setTags(tagsData.data || [])
      setBatches(Array.isArray(batchesData) ? batchesData : [])
      setWarranties(Array.isArray(warrantiesData) ? warrantiesData : [])
    } catch (err) {
      toastError('Failed to load assets', err)
    } finally {
      setLoading(false)
    }
  }, [toastError])

  useEffect(() => {
    if (enabled) loadData()
  }, [enabled, loadData])

  const handleGenerate = async () => {
    if (genForm.quantity < 1 || genForm.quantity > 500) {
      toast('Quantity must be between 1 and 500', 'error')
      return
    }
    setGenerating(true)
    try {
      await api.post('/asset-tracking/batches', {
        quantity: genForm.quantity,
        prefix: genForm.prefix,
        starting_number: genForm.starting_number,
        name: genForm.name || undefined,
      })
      toast(`Generated ${genForm.quantity} tags`)
      setShowGenModal(false)
      loadData()
    } catch (err) {
      toastError('Failed to generate tags', err)
    } finally {
      setGenerating(false)
    }
  }

  if (flagLoading) return <TableSkeleton cols={6} />
  if (!enabled) return <UpgradeBanner feature="Asset Tracking" plan={requiredPlan} description="Track installed devices with QR/barcode tags, warranty management, and lifecycle timelines." />

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Assets</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={PlusIcon} onClick={() => setShowGenModal(true)}>Generate Tags</Button>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Tags', value: stats.total_tags, color: 'var(--info)' },
            { label: 'Assigned', value: stats.assigned_tags, color: 'var(--ok)' },
            { label: 'In Stock', value: stats.unassigned_tags, color: 'var(--accent)' },
            { label: 'Warranties Expiring', value: stats.expiring_warranties, color: 'var(--err)' },
            { label: 'Scans (7d)', value: stats.recent_scans, color: 'var(--color-cable)' },
          ].map((s, i) => (
            <GlowCard key={s.label} style={{ padding: '12px 20px', minWidth: 110, textAlign: 'center', flex: 1 }} delay={100 + i * 80}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.label}</div>
            </GlowCard>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 1 }}>
        {([['tags', 'Tags'], ['warranties', 'Warranties'], ['batches', 'Batches']] as const).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: 'transparent', border: 'none',
              color: tab === t ? 'var(--accent-h)' : 'var(--text-secondary)',
              borderBottom: tab === t ? '2px solid var(--accent-h)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? <TableSkeleton cols={6} /> : (
        <>
          {/* Tags tab */}
          {tab === 'tags' && (
            tags.length === 0 ? (
              <EmptyState
                title="Start tracking assets"
                description="Generate a batch of asset tags to start tracking devices."
                action={{ label: 'Generate Tags', onClick: () => setShowGenModal(true) }}
              />
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Tag #</th>
                    <th>Short Code</th>
                    <th>Device</th>
                    <th>Site</th>
                    <th>Status</th>
                    <th>Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map(t => (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedTag(t); setEditForm({ status: t.status, device_id: t.device_id || '' }) }}>
                      <td style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 13 }}>{t.tag_number || '-'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>{t.short_code}</td>
                      <td>{t.device_name || <span style={{ color: 'var(--text-tertiary)' }}>Unassigned</span>}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.site_name || '-'}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: t.status === 'active' ? 'var(--ok-m)' : 'rgba(107, 114, 128, 0.15)',
                          color: t.status === 'active' ? 'var(--ok)' : 'var(--text-2)',
                          textTransform: 'uppercase',
                        }}>
                          {t.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {t.assigned_at ? new Date(t.assigned_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}

          {/* Warranties tab */}
          {tab === 'warranties' && (
            warranties.length === 0 ? (
              <EmptyState
                title="No warranties tracked"
                description="Add warranty info to devices to track expiration and coverage."
              />
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Site</th>
                    <th>Manufacturer</th>
                    <th>Type</th>
                    <th>Expires</th>
                    <th>Coverage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {warranties.map(w => {
                    const daysLeft = w.end_date ? Math.ceil((new Date(w.end_date).getTime() - Date.now()) / 86400000) : null
                    return (
                      <tr key={w.id}>
                        <td style={{ fontWeight: 600 }}>{w.device_name || w.device_id}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{w.site_name || '-'}</td>
                        <td style={{ fontSize: 13 }}>{w.manufacturer || '-'}</td>
                        <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{w.warranty_type}</td>
                        <td style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                          {w.end_date ? new Date(w.end_date).toLocaleDateString() : 'Lifetime'}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {w.covers_parts ? 'Parts' : ''}{w.covers_labor ? ' + Labor' : ''}
                        </td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                            background: daysLeft !== null && daysLeft < 30 ? 'var(--err-m)' : daysLeft !== null && daysLeft < 90 ? 'var(--warn-m)' : 'var(--ok-m)',
                            color: daysLeft !== null && daysLeft < 30 ? 'var(--err)' : daysLeft !== null && daysLeft < 90 ? 'var(--accent)' : 'var(--ok)',
                          }}>
                            {daysLeft !== null ? (daysLeft < 0 ? 'Expired' : `${daysLeft}d left`) : 'Active'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )
          )}

          {/* Batches tab */}
          {tab === 'batches' && (
            batches.length === 0 ? (
              <EmptyState
                title="No batches generated"
                description="Generate a batch of tags to print and deploy."
                action={{ label: 'Generate Batch', onClick: () => setShowGenModal(true) }}
              />
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Quantity</th>
                    <th>Assigned</th>
                    <th>In Stock</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600 }}>{b.name || `Batch ${new Date(b.created_at).toLocaleDateString()}`}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{b.prefix}</td>
                      <td>{b.quantity}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{b.tags_assigned}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{b.tags_in_stock}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{new Date(b.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </>
      )}

      {/* Tag Detail Modal */}
      {selectedTag && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        }}
          onClick={(e) => { if (e.target === e.currentTarget) { setSelectedTag(null); setEditingTag(false) } }}
        >
          <div style={{
            width: 480, maxWidth: '95vw', padding: 24, borderRadius: 16,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Tag Detail</h3>
              <button onClick={() => { setSelectedTag(null); setEditingTag(false) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}>&times;</button>
            </div>

            {/* QR Code placeholder */}
            <div id="tag-print-area" style={{ textAlign: 'center', marginBottom: 20, padding: 20, background: 'var(--bg-inset)', borderRadius: 12 }}>
              <svg width="120" height="120" viewBox="0 0 120 120" style={{ marginBottom: 8 }}>
                <rect width="120" height="120" fill="#fff" rx="8" />
                <text x="60" y="55" textAnchor="middle" fontSize="10" fill="#333" fontFamily="monospace">QR</text>
                <text x="60" y="70" textAnchor="middle" fontSize="8" fill="#666" fontFamily="monospace">{selectedTag.short_code}</text>
              </svg>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.05em' }}>
                {selectedTag.tag_number || selectedTag.short_code}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                ID: {selectedTag.short_code}
              </div>
            </div>

            {/* Detail info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Status</span>
                <span style={{ fontWeight: 600, color: 'var(--text)', textTransform: 'capitalize' }}>{selectedTag.status}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Device</span>
                <span style={{ fontWeight: 500, color: 'var(--text)' }}>{selectedTag.device_name || 'Unassigned'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Site</span>
                <span style={{ fontWeight: 500, color: 'var(--text)' }}>{selectedTag.site_name || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Created</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-tertiary)' }}>{new Date(selectedTag.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Edit form */}
            {editingTag && (
              <div style={{ padding: 16, background: 'var(--bg-inset)', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Status</label>
                  <div className="v3-select-wrap">
                    <select className="v3-select" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                      <option value="active">Active</option>
                      <option value="in_stock">In Stock</option>
                      <option value="retired">Retired</option>
                      <option value="lost">Lost</option>
                    </select>
                    <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await api.patch(`/asset-tracking/tags/${selectedTag.short_code}`, { status: editForm.status })
                      toast('Tag updated')
                      setEditingTag(false)
                      loadData()
                      setSelectedTag({ ...selectedTag, status: editForm.status })
                    } catch (err) {
                      toastError('Failed to update tag', err)
                    }
                  }}
                >
                  Save Changes
                </Button>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="secondary"
                size="sm"
                style={{ flex: 1 }}
                onClick={() => setEditingTag(!editingTag)}
              >
                {editingTag ? 'Cancel Edit' : 'Edit'}
              </Button>
              <Button
                size="sm"
                style={{ flex: 1 }}
                onClick={() => {
                  const printContent = document.getElementById('tag-print-area')
                  if (!printContent) return
                  const win = window.open('', '_blank')
                  if (!win) return
                  win.document.write(`<html><head><title>Print Tag</title><style>body{display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:monospace;}</style></head><body>`)
                  win.document.write(printContent.innerHTML)
                  win.document.write('</body></html>')
                  win.document.close()
                  win.print()
                }}
              >
                Print Tag
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Tags Modal */}
      {showGenModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowGenModal(false) }}
        >
          <div style={{
            width: 440, maxWidth: '95vw', padding: 24, borderRadius: 16,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 20 }}>Generate Asset Tags</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Batch Name</label>
                <input className="v3-input" value={genForm.name} onChange={e => setGenForm({ ...genForm, name: e.target.value })} placeholder="March 2026 Deploy" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Quantity</label>
                  <input className="v3-input" type="number" min="1" max="500" value={genForm.quantity} onChange={e => setGenForm({ ...genForm, quantity: parseInt(e.target.value) || 1 })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Prefix</label>
                  <div className="v3-select-wrap">
                    <select className="v3-select" value={genForm.prefix} onChange={e => setGenForm({ ...genForm, prefix: e.target.value })}>
                      <option value="TAG">TAG</option>
                      <option value="CAM">CAM</option>
                      <option value="ACC">ACC</option>
                      <option value="SW">SW</option>
                      <option value="AP">AP</option>
                      <option value="FW">FW</option>
                    </select>
                    <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Start #</label>
                  <input className="v3-input" type="number" min="1" value={genForm.starting_number} onChange={e => setGenForm({ ...genForm, starting_number: parseInt(e.target.value) || 1 })} />
                </div>
              </div>

              <div style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--bg-inset)', fontSize: 12, color: 'var(--text-secondary)' }}>
                Preview: <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)' }}>
                  {genForm.prefix}-{String(genForm.starting_number).padStart(3, '0')}
                </span> through <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)' }}>
                  {genForm.prefix}-{String(genForm.starting_number + genForm.quantity - 1).padStart(3, '0')}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <button onClick={() => setShowGenModal(false)} style={{
                padding: '8px 16px', borderRadius: 8, background: 'transparent',
                border: '1px solid var(--border)', color: 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}>
                Cancel
              </button>
              <Button onClick={handleGenerate} loading={generating}>
                Generate {genForm.quantity} Tags
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
